"use client";

import NumberFlow from "@number-flow/react";
import { useInView } from "react-intersection-observer";
import { PopText, Reveal } from "@/components/motion/reveal";
import { AssetIcon } from "@/components/ui/asset-icon";
import { GlassCard } from "@/components/ui/glass-card";
import { GradientText } from "@/components/ui/gradient-text";
import { Spotlight } from "@/components/ui/spotlight";
import { CHAINS, STATS, type Stat } from "@/lib/landing-data";

function StatCounter({ stat }: { stat: Stat }) {
  // Viewport detection via react-intersection-observer (the one detection lib);
  // NumberFlow handles the count-up easing once we're in view.
  const { ref, inView } = useInView({ triggerOnce: true, rootMargin: "0px 0px -10% 0px" });
  const hasDecimal = stat.value % 1 !== 0;
  return (
    <div ref={ref}>
      <GlassCard className="flex flex-col items-center gap-1 px-4 py-8 text-center">
        <div className="text-4xl font-semibold tabular-nums sm:text-5xl">
          {stat.prefix}
          <NumberFlow
            value={inView ? stat.value : 0}
            format={{ maximumFractionDigits: hasDecimal ? 1 : 0 }}
          />
          {stat.suffix}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{stat.label}</p>
      </GlassCard>
    </div>
  );
}

export function Stats() {
  return (
    <section id="stats" className="relative overflow-hidden px-4 py-16 sm:py-20">
      <Spotlight fill="rgba(168,85,247,0.14)" className="left-1/2 top-1/3 -translate-x-1/2" />

      <Reveal className="mx-auto mb-12 max-w-2xl text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Trusted at scale</p>
        <h2 className="mt-3 text-balance text-4xl font-semibold tracking-tight sm:text-5xl">
          <PopText className="block">One gate.</PopText>
          <PopText className="block" delay={0.18}>
            <GradientText>Serious scale.</GradientText>
          </PopText>
        </h2>
      </Reveal>

      <div className="mx-auto grid max-w-5xl grid-cols-2 gap-4 lg:grid-cols-4">
        {STATS.map((stat) => (
          <StatCounter key={stat.label} stat={stat} />
        ))}
      </div>

      <div className="mx-auto mt-12 flex max-w-3xl flex-wrap items-center justify-center gap-x-6 gap-y-3">
        {CHAINS.map((chain) => (
          <span key={chain.id} className="flex items-center gap-2 text-sm text-muted-foreground">
            <AssetIcon
              asset={{ symbol: chain.name, logo: chain.logo, color: chain.color }}
              size={22}
            />
            {chain.name}
          </span>
        ))}
      </div>

      <p className="mt-10 text-center text-sm text-muted-foreground">
        No seed phrases · One login
      </p>
    </section>
  );
}
